import { API_BASE } from './client'
import type { ServerEvent } from './types'

export type WsStatus = 'connecting' | 'open' | 'closed'

type EventHandler = (event: ServerEvent) => void
type StatusHandler = (status: WsStatus) => void

function wsBase(): string {
  return API_BASE
    ? API_BASE.replace(/^http/, 'ws')
    : `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}`
}

/** Подписка на события проекта (редактор). */
export function projectWsUrl(projectId: string): string {
  return `${wsBase()}/api/ws?projectId=${encodeURIComponent(projectId)}`
}

/** Подписка на события комнаты: лобби и сессия. Принимает код или id. */
export function roomWsUrl(room: string): string {
  return `${wsBase()}/api/ws?roomId=${encodeURIComponent(room)}`
}

function isServerEvent(value: unknown): value is ServerEvent {
  return typeof value === 'object' && value !== null && typeof (value as { type?: unknown }).type === 'string'
}

const MAX_BACKOFF_MS = 15_000

/**
 * WS-клиент с автопереподключением (экспоненциальный backoff).
 * Недоступный сервер не считается фатальной ошибкой: клиент просто продолжает
 * пытаться, а UI работает по HTTP-поллингу.
 */
export class ProjectSocket {
  private readonly url: string
  private socket: WebSocket | null = null
  private readonly eventHandlers = new Set<EventHandler>()
  private readonly statusHandlers = new Set<StatusHandler>()
  private attempt = 0
  private reconnectTimer: number | null = null
  private disposed = false
  private status: WsStatus = 'closed'

  constructor(url: string) {
    this.url = url
  }

  connect(): void {
    if (this.disposed || this.socket) return
    this.setStatus('connecting')
    let socket: WebSocket
    try {
      socket = new WebSocket(this.url)
    } catch {
      this.scheduleReconnect()
      return
    }
    this.socket = socket

    socket.onopen = (): void => {
      this.attempt = 0
      this.setStatus('open')
    }
    socket.onmessage = (ev: MessageEvent<unknown>): void => {
      if (typeof ev.data !== 'string') return
      let parsed: unknown
      try {
        parsed = JSON.parse(ev.data) as unknown
      } catch {
        return
      }
      if (!isServerEvent(parsed)) return
      for (const handler of this.eventHandlers) handler(parsed)
    }
    socket.onerror = (): void => {
      // Разрыв обработаем в onclose — здесь только глушим необработанную ошибку.
    }
    socket.onclose = (): void => {
      this.socket = null
      this.setStatus('closed')
      this.scheduleReconnect()
    }
  }

  onEvent(handler: EventHandler): () => void {
    this.eventHandlers.add(handler)
    return () => this.eventHandlers.delete(handler)
  }

  onStatus(handler: StatusHandler): () => void {
    this.statusHandlers.add(handler)
    handler(this.status)
    return () => this.statusHandlers.delete(handler)
  }

  dispose(): void {
    this.disposed = true
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.eventHandlers.clear()
    this.statusHandlers.clear()
    const socket = this.socket
    this.socket = null
    if (socket) {
      socket.onopen = null
      socket.onmessage = null
      socket.onerror = null
      socket.onclose = null
      if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
        socket.close()
      }
    }
  }

  private setStatus(status: WsStatus): void {
    if (this.status === status) return
    this.status = status
    for (const handler of this.statusHandlers) handler(status)
  }

  private scheduleReconnect(): void {
    if (this.disposed || this.reconnectTimer !== null) return
    const delay = Math.min(MAX_BACKOFF_MS, 500 * 2 ** this.attempt) + Math.random() * 250
    this.attempt = Math.min(this.attempt + 1, 6)
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null
      this.connect()
    }, delay)
  }
}
